/* eslint-disable object-shorthand */
/* eslint-disable func-names */
/* eslint-disable react/no-this-in-sfc */
import { useQuery } from "@apollo/client";
import type { RefObjectForHighchartsReact } from "components/charts/modern/abstract/HighchartsChart";
import HighchartsChart from "components/charts/modern/abstract/HighchartsChart";
import afterSetExtremes from "components/charts/modern/highcharts/afterSetExtremes";
import darkNavigatorStyles from "components/charts/modern/highcharts/darkNavigatorStyles";
import navigatorStyles from "components/charts/modern/highcharts/navigatorStyles";
import TickerSymbolSelector from "components/charts/modern/inputs/TickerSymbolSelector";
import type { ColorTheme } from "components/charts/modern/quotes/getColorThemeForIndex";
import getColorThemeForIndex from "components/charts/modern/quotes/getColorThemeForIndex";
import useRootIssuer from "components/company/v3/context/hooks/useRootIssuer";
import OnlyPremiumRender from "components/conversion/OnlyPremiumRender";
import SelectStatementType from "components/financials/modern/SelectStatementType";
import FinancialsSeriesQuery from "components/financials/modern/graphql/FinancialsSeriesQuery";
import type {
  IFinancialsSeriesQuery,
  IFinancialsSeriesQueryVariables,
} from "components/financials/modern/graphql/__generated__/FinancialsSeriesQuery.generated";
import getIndicatorDocumentation from "components/financials/modern/indicators/getIndicatorDocumentation";
import getPlatformVerticalByIssuer from "components/financials/modern/utils/platformVertical";
import SadFaceIcon from "components/icons/SadFaceIcon";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/Select";
import classNames from "components/ui/classNames";
import aeonik from "components/utils/aeonik";
import { endOfYear, lightFormat } from "date-fns";
import compareAsc from "date-fns/compareAsc";
import parseISO from "date-fns/parseISO";
import type Highcharts from "highcharts/highstock";
import type { SeriesLineOptions, YAxisOptions } from "highcharts/highstock";
import type { IAmericanCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/AmericanCompanyIssuerTopLevel.generated";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import type { IBrazilianRealEstateFundTopLevelFragment } from "lib/fragments/__generated__/BrazilianRealEstateFundTopLevel.generated";
import useSelfie from "lib/hooks/useSelfie";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import flatten from "lodash/flatten";
import { invariant } from "outvariant";
import { useEffect, useRef, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import type { IntlShape } from "react-intl";
import { FormattedMessage, useIntl } from "react-intl";
import colors from "tailwindcss/colors";
import type { ISecurityCountry } from "types/graphqlTypes";
import { IFinancialsStatementType, IIndicatorDataSource, IPlatformVertical } from "types/graphqlTypes";
import useTheme from "components/ui/useTheme";

const PRICE_INDICATOR_ID = "ind_01h64v4et4fwxttehta5m5pchb";
const NET_INCOME_CVM_INDICATOR_ID = "ind_01hqv3nmtyfsn9w9ke0q8n73b1";
const EBITDA_CVM_INDICATOR_ID = "ind_01h7zcjce9eqp8xb8we1eqa0wq";
const NET_INCOME_VENDOR_INDICATOR_ID = "ind_01h64d63cgfvc9h1z91p30j8cn";
const EBITDA_VENDOR_INDICATOR_ID = "ind_01h64d6a0yf56vnve0y71dqxm2";
const REPURCHASE_VENDOR_INDICATOR_ID = "ind_01h64d6xazeew8ybphdnk148mq";
const OCF_VENDOR_INDICATOR_ID = "ind_01h64d74qde9sv3z12rvxsf8q1";
const OCF_PER_SHARE_VENDOR_INDICATOR_ID = "ind_01h64tngtrfg18gzkvrn1cgeep";
const YIELD_PER_SHARE_ID = "ind_01hr5y3egee20v86ckb6sa73cr";
const EPS_INDICATOR_ID = "ind_01k5c2wmd0ef584tzdvkr8tjzf";

type IndicatorVsQuoteAxisCutomData = {
  asOfDate: string;
  indicatorName: string;
  value: number;
  formattedValue: string;
  theme: {
    color: string;
    lineColor: string;
  };
};

type IndicatorPair = [string, string];

interface IndicatorVsQuoteChartProps {
  tickerSymbols: string[];
  country: ISecurityCountry;
  availableTickerSymbols?: string[];
  onToggleTickerSymbols?: (tickerSymbol: string[]) => void;
}

type IndicatorPairSettings = {
  slug: string;
  label: string;
  objectIds: IndicatorPair;
  platformVertical: IPlatformVertical[];
  dataSource: IIndicatorDataSource;
  absoluteTransform?: boolean;
  themeOverride?: Record<string, ColorTheme>;
  filterByReportDateAfter?: string | null;
};

export default function IndicatorVsQuoteChart({
  tickerSymbols,
  country,
  availableTickerSymbols,
  onToggleTickerSymbols,
}: IndicatorVsQuoteChartProps) {
  if (tickerSymbols.length === 0) {
    throw new Error("Couldn't locate the selected ticker symbol");
  }

  const chartRef = useRef<RefObjectForHighchartsReact | null>(null);
  const [statementType, setStatementType] = useState<IFinancialsStatementType>(IFinancialsStatementType.Annual);

  const intl = useIntl();
  const viewer = useSelfie();
  const issuer = useRootIssuer();
  const theme = useTheme();

  const selectedTickerSymbol = tickerSymbols[0];
  const platformVertical = getPlatformVerticalByIssuer(issuer);
  const isStocksOrReits = platformVertical === IPlatformVertical.Stocks || platformVertical === IPlatformVertical.Reits;
  const isFiis = platformVertical === IPlatformVertical.Fiis;

  const indicatorsPairs = getIndicatorPairs(issuer, platformVertical, intl);

  const [indicatorPair, setIndicatorPair] = useState<IndicatorPair | null>(() => {
    return indicatorsPairs.length > 0 ? indicatorsPairs[0].objectIds : null;
  });
  invariant(indicatorPair, "Faulty data");

  const selectedPair = indicatorsPairs.find((candidate) => {
    return candidate.objectIds.every((objectId) => {
      return indicatorPair?.includes(objectId);
    });
  });

  invariant(selectedPair, "Faulty data");

  const { loading, data, previousData } = useQuery<IFinancialsSeriesQuery, IFinancialsSeriesQueryVariables>(
    FinancialsSeriesQuery,
    {
      variables: {
        currency: isStocksOrReits ? "USD" : "BRL",
        statementType:
          statementType === IFinancialsStatementType.Quarterly ? IFinancialsStatementType.Ttm : statementType,
        vertical: platformVertical,
        indicatorDataSource: selectedPair.dataSource,
        filterByInterim: null,
        indicators: indicatorPair,
        limit: viewer?.isTrialing ? 10 : 240,
        securities: [
          {
            country,
            tickerSymbol: selectedTickerSymbol,
          },
        ],
        filterByReportDateAfter: selectedPair.filterByReportDateAfter || null,
        filterByReportDateBefore: selectedPair.filterByReportDateAfter
          ? lightFormat(endOfYear(new Date()), "yyyy-MM-dd")
          : null,
      },
    },
  );

  const series = (loading && !data ? previousData?.financialsSeries.series : data?.financialsSeries.series) || [];

  const hasSeries = series.length > 0;
  const hasSeriesData = series.every((node) => {
    return node.indicators.every((indicator) => {
      return indicator.series.length > 0;
    });
  });

  const [financialIndicators] = series;
  const options = hasSeries ? createHighchartsOptions(intl, financialIndicators, selectedPair, theme) : null;

  useEffect(() => {
    if (chartRef && chartRef.current) {
      chartRef.current.chart.yAxis.forEach((axis) => {
        const points = flatten(axis.series.map((series) => series.points.map((node) => node.y)));

        const values = points.filter(notUndefinedOrNull);

        const minValue = Math.min(...values);
        const maxValue = Math.max(...values);

        axis.setExtremes(minValue, maxValue, true, false);
      });
    }
  }, [chartRef, data]);

  if (loading && !hasSeries) {
    return <LoadingSkeleton count={1} className="min-h-96 xl:h-full" />;
  }

  if (!selectedPair || (!loading && !hasSeriesData)) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <div className="space-y-2 text-center text-base tracking-wide text-gray-600 dark:text-gray-400">
          <div>
            <SadFaceIcon />
          </div>

          <p>
            <FormattedMessage defaultMessage="Nenhum dado foi encontrado." id="i1Elih" />
          </p>
        </div>
      </div>
    );
  }

  return (
    <OnlyPremiumRender>
      <div
        className={classNames("flex h-full w-full flex-col", {
          "space-y-4": !isFiis,
        })}
      >
        <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
          <div
            className={classNames("grid w-full grid-cols-[1fr_0.25fr] items-center gap-4 md:flex", {
              "grid-cols-1": indicatorsPairs.length === 1,
            })}
          >
            {onToggleTickerSymbols && availableTickerSymbols && availableTickerSymbols.length >= 2 && (
              <div
                className={classNames({
                  "col-span-2": indicatorsPairs.length > 1,
                })}
              >
                <TickerSymbolSelector
                  tickerSymbols={availableTickerSymbols}
                  value={tickerSymbols}
                  onChange={(tickerSymbols) => onToggleTickerSymbols(tickerSymbols)}
                />
              </div>
            )}

            {indicatorsPairs.length > 1 && (
              <Select
                value={selectedPair.slug || undefined}
                onValueChange={(value) => {
                  const pair = indicatorsPairs.find((candidate) => {
                    return candidate.slug === value;
                  });

                  if (pair) {
                    setIndicatorPair(pair.objectIds);
                  }
                }}
              >
                <SelectTrigger
                  className={classNames(
                    "font-aeonik",
                    "w-full rounded-xl sm:w-56",
                    "border border-slate-200 bg-white",
                    "xl:font-sm text-xs font-medium tracking-wide text-slate-600",
                    "disabled:select-none",
                    "hover:border-transparent hover:ring-slate-100 hover:ring-offset-1 hover:ring-offset-slate-300",
                    "focus-visible:border-transparent focus-visible:ring-4 focus-visible:ring-offset-1",
                    "focus-visible:ring-indigo-100 focus-visible:ring-offset-indigo-300",
                    "truncate",
                    "dark:border-slate-950 dark:bg-slate-700 dark:text-slate-300",
                    "dark:hover:ring-slate-900 dark:hover:ring-offset-slate-900",
                    "dark:focus-visible:ring-slate-900 dark:focus-visible:ring-offset-slate-900",
                  )}
                >
                  <SelectValue
                    placeholder={intl.formatMessage({
                      defaultMessage: "Selecionar",
                      description: "Valor padrão do campo",
                      id: "1DaLxH",
                    })}
                  >
                    {selectedPair.label}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="max-h-52 rounded-lg border border-slate-300 text-sm shadow-xl" side="top">
                  {indicatorsPairs.map((pair) => (
                    <SelectItem key={pair.slug} value={pair.slug}>
                      {pair.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {!isFiis && !viewer?.isTrialing && (
              <SelectStatementType
                disabled={loading || !viewer?.isPremium}
                statementType={statementType}
                onChangeStatementType={setStatementType}
              />
            )}
          </div>

          <p className="whitespace-nowrap text-xs italic tracking-wider">
            *<FormattedMessage defaultMessage="Indicadores na escala de milhões" id="jIcKHj" />
          </p>
        </div>

        {hasSeries && options ? (
          <HighchartsChart immutable={true} ref={chartRef} options={options} className="h-96 xl:h-full" />
        ) : (
          <div className="flex h-96 w-80 items-center justify-center sm:w-full">
            <div className="space-y-2 text-center text-base tracking-wide text-gray-600 dark:text-gray-400">
              <div>
                <SadFaceIcon />
              </div>

              <p>
                <FormattedMessage defaultMessage="Nenhum dado foi encontrado." id="i1Elih" />
              </p>
            </div>
          </div>
        )}
      </div>
    </OnlyPremiumRender>
  );
}

function getIndicatorPairs(
  issuer:
    | IAmericanCompanyIssuerTopLevelFragment
    | IBrazilianCompanyIssuerTopLevelFragment
    | IBrazilianRealEstateFundTopLevelFragment,
  platformVertical: IPlatformVertical,
  intl: IntlShape,
) {
  const isBanks =
    (issuer.__typename === "BrazilianCompanyIssuer" && issuer.sector === "Bancos") ||
    (issuer.__typename === "AmericanCompanyIssuer" && issuer.sector === "Banks");

  const INDICATOR_PAIRS = [
    {
      slug: "PRICE_VS_NET_INCOME",
      label: intl.formatMessage({
        defaultMessage: "Cotação x Lucro",
        id: "c/9YDg",
      }),
      objectIds: [PRICE_INDICATOR_ID, NET_INCOME_CVM_INDICATOR_ID],
      platformVertical: [IPlatformVertical.Acoes],
      dataSource: IIndicatorDataSource.Cvm,
    },
    {
      slug: "PRICE_VS_NET_INCOME",
      label: intl.formatMessage({
        defaultMessage: "Cotação x Lucro",
        id: "c/9YDg",
      }),
      objectIds: [PRICE_INDICATOR_ID, NET_INCOME_VENDOR_INDICATOR_ID],
      platformVertical: [IPlatformVertical.Stocks],
      dataSource: IIndicatorDataSource.Morningstar,
    },
    {
      slug: "PRICE_VS_OCF",
      label: intl.formatMessage({
        defaultMessage: "Cotação x FCO",
        id: "TSTTKa",
      }),
      objectIds: [PRICE_INDICATOR_ID, OCF_VENDOR_INDICATOR_ID],
      platformVertical: [IPlatformVertical.Reits],
      dataSource: IIndicatorDataSource.Morningstar,
    },
    {
      slug: "PRICE_VS_OCFS",
      label: intl.formatMessage({
        defaultMessage: "Cotação x FCOS",
        id: "2m+Psp",
      }),
      objectIds: [PRICE_INDICATOR_ID, OCF_PER_SHARE_VENDOR_INDICATOR_ID],
      platformVertical: [IPlatformVertical.Reits],
      dataSource: IIndicatorDataSource.Morningstar,
    },
    {
      slug: "PRICE_VS_EPS",
      label: intl.formatMessage({
        defaultMessage: " Cotação x LPA",
        id: "pxXu0H",
      }),
      objectIds: [PRICE_INDICATOR_ID, EPS_INDICATOR_ID],
      platformVertical: [IPlatformVertical.Stocks],
      dataSource: IIndicatorDataSource.Morningstar,
    },
    {
      slug: "PRICE_VS_EPS",
      label: intl.formatMessage({
        defaultMessage: " Cotação x LPA",
        id: "pxXu0H",
      }),
      objectIds: [PRICE_INDICATOR_ID, EPS_INDICATOR_ID],
      platformVertical: [IPlatformVertical.Acoes],
      dataSource: IIndicatorDataSource.Cvm,
      filterByReportDateAfter: "2012-01-01", // Only show EPS for CVM after 2012
    },
    isBanks
      ? null
      : {
          slug: "PRICE_VS_EBITDA",
          label: intl.formatMessage({
            defaultMessage: "Cotação x EBITDA",
            id: "c+D5KT",
          }),
          objectIds: [PRICE_INDICATOR_ID, EBITDA_CVM_INDICATOR_ID],
          platformVertical: [IPlatformVertical.Acoes],
          dataSource: IIndicatorDataSource.Cvm,
        },

    // Vendor
    isBanks
      ? null
      : {
          slug: "PRICE_VS_EBITDA",
          label: intl.formatMessage({
            defaultMessage: "Cotação x EBITDA",
            id: "c+D5KT",
          }),
          objectIds: [PRICE_INDICATOR_ID, EBITDA_VENDOR_INDICATOR_ID],
          platformVertical: [IPlatformVertical.Stocks, IPlatformVertical.Reits],
          dataSource: IIndicatorDataSource.Morningstar,
        },
    {
      slug: "PRICE_VS_REPURCHASE_OF_CAPITAL_STOCK",
      label: intl.formatMessage({
        defaultMessage: "Cotação x Recompra",
        id: "lxKdQ3",
      }),
      objectIds: [PRICE_INDICATOR_ID, REPURCHASE_VENDOR_INDICATOR_ID],
      platformVertical: [IPlatformVertical.Stocks, IPlatformVertical.Reits],
      dataSource: IIndicatorDataSource.Morningstar,
      absoluteTransform: true,
    },
    {
      slug: "REPURCHASE_OF_CAPITAL_STOCK_VS_CASH_DIVIDENDS_PAID",
      label: intl.formatMessage({
        defaultMessage: "Recompra x Dividendos",
        id: "AR4KwP",
      }),
      objectIds: ["ind_01h64d6y7zfbxsncp4642k2svh", REPURCHASE_VENDOR_INDICATOR_ID],
      platformVertical: [IPlatformVertical.Stocks, IPlatformVertical.Reits],
      dataSource: IIndicatorDataSource.Morningstar,
      absoluteTransform: true,
      themeOverride: {
        // Repurchase of Capital Stock
        ind_01h64d6xazeew8ybphdnk148mq: {
          color: colors.blue[50],
          lineColor: colors.blue[600],
        },
        // Cash Dividends Paid
        ind_01h64d6y7zfbxsncp4642k2svh: {
          color: colors.orange[50],
          lineColor: colors.orange[600],
        },
      } as Record<string, ColorTheme>,
    },

    // FNET
    {
      slug: "PRICE_VS_YIELD_PER_SHARE",
      label: intl.formatMessage({
        defaultMessage: "Cotação x Rendimentos por Cota",
        id: "7SEqtc",
      }),
      objectIds: [PRICE_INDICATOR_ID, YIELD_PER_SHARE_ID],
      platformVertical: [IPlatformVertical.Fiis],
      dataSource: IIndicatorDataSource.Fnet,
    },
  ].filter(notUndefinedOrNull) as Array<IndicatorPairSettings>;

  return INDICATOR_PAIRS.filter((candidate) => candidate.platformVertical.includes(platformVertical));
}

function createHighchartsOptions(
  intl: IntlShape,
  financialIndicators: IFinancialsSeriesQuery["financialsSeries"]["series"][0],
  indicatorPair: IndicatorPairSettings | null,
  currentTheme: "dark" | "light",
) {
  // Theme-aware colors
  const slateOrDarkSlate = currentTheme === "dark" ? colors.slate[800] : colors.slate[100];
  const labelsColor = currentTheme === "dark" ? colors.slate[300] : colors.slate[500];
  const crosshairColor = currentTheme === "dark" ? colors.slate[500] : colors.slate[800];
  const tooltipBgColor = currentTheme === "dark" ? colors.slate[800] : colors.white;
  const tooltipBorderColor = currentTheme === "dark" ? colors.slate[950] : colors.slate[200];
  const hoverBorderColor = currentTheme === "dark" ? colors.slate[400] : colors.slate[500];
  const hoverFillColor = currentTheme === "dark" ? colors.slate[700] : colors.slate[50];
  const lineColor = currentTheme === "dark" ? colors.teal[400] : colors.teal[600];
  const markerFillColor = currentTheme === "dark" ? colors.teal[900] : colors.teal[50];
  const quotes = structuredClone(
    financialIndicators.indicators.find((indicator) => indicator.objectId === PRICE_INDICATOR_ID)?.series || [],
  );

  const firstValidQuoteIndex =
    [...quotes].reverse().findIndex((node) => {
      let value = 0;
      if (
        node.__typename !== "FinancialIndicatorSeriesTextNode" &&
        node.__typename !== "FinancialIndicatorSeriesSkeletonNode"
      ) {
        value = indicatorPair?.absoluteTransform ? Math.abs(node.value) : node.value;
      }

      return value > 0;
    }) || 0;
  const firstValidQuoteDate = structuredClone(financialIndicators.reportDates).reverse()[firstValidQuoteIndex];

  const series = (indicatorPair?.objectIds || [])
    .map((objectId) => {
      return financialIndicators.indicators.find((indicator) => indicator.objectId === objectId);
    })
    .filter(notUndefinedOrNull)
    .map((indicator, index) => {
      const indicatorName = getIndicatorDocumentation(intl, indicator).name;

      const theme =
        indicatorPair?.themeOverride && indicatorPair.themeOverride[indicator.objectId]
          ? indicatorPair.themeOverride[indicator.objectId]
          : getColorThemeForIndex(indicator.objectId === PRICE_INDICATOR_ID ? 0 : 1, currentTheme);

      invariant(theme, "Theme not found");

      return {
        showInNavigator: index === 0,
        type: "line",
        animation: false,
        name: indicatorName,
        yAxis: index,
        color: theme.lineColor,
        // lineColor: theme.lineColor,
        lineWidth: 2,
        data: financialIndicators.reportDates
          .map((date, index) => {
            const node = indicator.series[index];
            if (!node) {
              return null;
            }

            let value = 0;
            if (
              node.__typename !== "FinancialIndicatorSeriesTextNode" &&
              node.__typename !== "FinancialIndicatorSeriesSkeletonNode"
            ) {
              value = indicatorPair?.absoluteTransform ? Math.abs(node.value) : node.value;
            }

            return {
              x: parseISO(date).getTime(),
              y: value > 0 ? value : 0,
              custom: {
                asOfDate: date,
                indicatorName,
                value,
                formattedValue: indicator.series[index].formattedValue,
                theme,
              },
            };
          })
          .filter(notUndefinedOrNull)
          .filter((node) => {
            if (indicator.objectId === PRICE_INDICATOR_ID) {
              return node.y > 0;
            }
            if (firstValidQuoteDate) {
              return node.x >= parseISO(firstValidQuoteDate).getTime();
            }
            return true;
          })
          .sort((a, b) => compareAsc(a.x, b.x)),
        custom: {
          primaryColor: theme.color,
          secondaryColor: theme.lineColor,
        },
        marker: {
          width: 8,
          height: 8,
          lineWidth: 1,
          fillColor: theme.color,
          color: theme.color,
          lineColor: theme.lineColor,
          symbol: "circle",
        },
      } satisfies SeriesLineOptions;
    })
    .filter(notUndefinedOrNull);

  const options: Highcharts.Options = {
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    chart: {
      className: "font-aeonik font-normal",
      backgroundColor: "transparent",
      panning: {
        enabled: false,
      },
      alignTicks: true,
      zooming: {
        mouseWheel: false,
        singleTouch: false,
      },
      borderWidth: 0,
      height: null,
      plotBorderWidth: 1,
      plotBorderColor: slateOrDarkSlate,
    },
    scrollbar: {
      enabled: false,
    },
    xAxis: {
      zoomEnabled: false,
      gridLineWidth: 1,
      gridLineColor: slateOrDarkSlate,
      lineWidth: 1,
      lineColor: slateOrDarkSlate,
      tickWidth: 0,
      scrollbar: {
        enabled: false,
      },
      labels: {
        style: {
          color: labelsColor,
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
      crosshair: {
        snap: true,
        dashStyle: "Dash",
        color: crosshairColor,
      },
      events: {
        afterSetExtremes: function (this, event) {
          afterSetExtremes(this, event);
        },
      },
    },
    yAxis: (indicatorPair?.objectIds || [])
      .map((objectId) => financialIndicators.indicators.find((indicator) => indicator.objectId === objectId))
      .filter(notUndefinedOrNull)
      .map((indicator, index) => {
        const minValues = indicator.series
          .map((node) => {
            if (
              node.__typename !== "FinancialIndicatorSeriesTextNode" &&
              node.__typename !== "FinancialIndicatorSeriesSkeletonNode"
            ) {
              return indicatorPair?.absoluteTransform ? Math.abs(node.value) : node.value;
            }

            return null;
          })
          .filter(notUndefinedOrNull);

        const minValue = minValues.length > 0 ? Math.min(...minValues) : 0;

        return {
          type: "linear",
          min: minValue > 0 ? minValue : 0,
          gridLineColor: slateOrDarkSlate,
          lineWidth: 1,
          lineColor: slateOrDarkSlate,
          labels: {
            enabled: true,
            align: "center",
            style: {
              color: labelsColor,
              fontFamily: aeonik.style.fontFamily,
              fontWeight: "500",
              fontSize: "12px",
            },
          },
          tickWidth: 0,
          showFirstLabel: true,
          showLastLabel: false,
          scrollbar: {
            enabled: false,
          },
          opposite: index === 1,
          crosshair: {
            snap: true,
            dashStyle: "Dash",
            color: crosshairColor,
          },
        } satisfies YAxisOptions;
      })
      .filter(notUndefinedOrNull),
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      ...(currentTheme === "dark" ? darkNavigatorStyles : navigatorStyles),
      yAxis: {
        ...(currentTheme === "dark" ? darkNavigatorStyles : navigatorStyles).yAxis,
        events: {
          afterSetExtremes(this, event) {
            this.setExtremes(event.dataMin, event.dataMax);
          },
        },
        type: "linear",
      },
    },
    series: [...series],
    tooltip: {
      followPointer: true,
      followTouchMove: true,
      enabled: true,
      shadow: {
        color: "rgba(0, 0, 0, 0.1)",
      },
      backgroundColor: tooltipBgColor,
      borderColor: tooltipBorderColor,
      borderWidth: 1,
      padding: 0,
      valueDecimals: 2,
      borderRadius: 12,
      shape: "callout",
      useHTML: true,
      split: false,
      shared: true,
      formatter(this: Highcharts.TooltipFormatterContextObject) {
        if (!this.points || this.points.length === 0) {
          return "";
        }

        const point = (this.point as any).custom as IndicatorVsQuoteAxisCutomData | null | undefined;
        if (!this.key || !point) {
          return "";
        }

        return renderToStaticMarkup(
          <div className="z-10 flex flex-col space-y-3 p-2 font-mono">
            <span className="inline-flex justify-center rounded-lg bg-slate-50 px-2 py-1 text-xs font-bold text-slate-600 dark:bg-slate-900 dark:text-slate-300">
              {intl.formatDate(new Date(point.asOfDate), {
                year: "numeric",
                month: "numeric",
                day: "numeric",
              })}
            </span>

            {this.points
              .sort((a, b) => {
                return (b.y || 0) - (a.y || 0);
              })
              .map(({ key, series, point }) => {
                const node = (point as any).custom as IndicatorVsQuoteAxisCutomData | null | undefined;
                if (!key || !node) {
                  return "";
                }

                return (
                  <div key={node.theme.color} className="flex items-center justify-between space-x-3">
                    <span
                      className="rounded-md px-2 py-1 font-mono text-xs font-bold"
                      style={{
                        backgroundColor: node.theme.color,
                        color: node.theme.lineColor,
                      }}
                    >
                      {series.name}
                    </span>

                    <span className="font-mono font-semibold dark:text-slate-300">{node.formattedValue}</span>
                  </div>
                );
              })}
          </div>,
        );
      },
    },
    plotOptions: {
      series: {
        enableMouseTracking: true,
        states: {
          hover: {
            borderColor: hoverBorderColor,
            color: hoverFillColor,
            lineWidth: 2,
          },
        },
      },
      line: {
        color: lineColor,
        lineWidth: 2,
        dataLabels: {
          enabled: false,
        },
        marker: {
          enabled: false,
          fillColor: markerFillColor,
          lineColor: lineColor,
          lineWidth: 4,
          width: 8,
          height: 8,
        },
      },
    },
  };

  return options;
}
